import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtYYYYMMDDHHmmss, useFormatWithSettings} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SearchType} from '../types';

interface Series {
  name: string;
  data: Record<string, any>[];
  type: string;
  colorBy?: string;
  rippleEffect?: {
    number: number;
    scale: number;
    period: number;
  };
}

/**
 * 引数
 */
interface MatchingChartProps {
  title: string;
  source: Record<string, any>[];
  xlist: string[];
  searchType: SearchType;
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;

  onClick?: (prm: {data: any}) => void;
}

/**
 * MatchingChart
 * @param props
 * @returns
 */
export const MatchingChart = (props: MatchingChartProps) => {
  const {t} = useTranslation();
  const {fmtPercent} = useFormatWithSettings();
  const {parsePercent} = useParseWithSettings();

  const [series, setSeries] = useState<Series[]>([]);
  const [exportFields, setExportFields] = useState<Record<string, any>>({});
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);
  const [xAxisData, setXAxisData] = useState<string[]>([]);

  useEffect(() => {
    if (isEmpty(props.source)) return;

    const tmpSeries: Series[] = [];

    if (props.searchType === SearchType.WORK) {
      const xline: string[] = [];
      const datas: Record<string, any>[] = [];
      props.source.forEach(r => {
        xline.push(r.x_timeline);
        datas.push({
          value: parsePercent(r.minv),
          lq_time: r.lq_time,
          serial: r.serial,
          uuid: r.uuid,
          branch_no: r.branch_no,
          map: r.map
        });
      });

      setXAxisData(xline);
      tmpSeries.push({
        name: '',
        data: datas,
        type: 'effectScatter',
        colorBy: 'series',
        rippleEffect: {
          number: 0,
          scale: 0,
          period: 0
        }
      });
    } else {
      setXAxisData(props.xlist);
      const branchList = Array.from(new Set(props.source.map((r: any) => Number(r.branch_no))));

      branchList.forEach(branchNo => {
        const datas: Record<string, any>[] = [];
        props.xlist.forEach((xl: string) => {
          const filteredRow = props.source.find(
            (r: any) => r.x_timeline === xl && r.branch_no === branchNo
          );
          if (isEmpty(filteredRow)) {
            datas.push({});
            return;
          }
          datas.push({
            value: [
              parsePercent(filteredRow.minv),
              parsePercent(filteredRow.q1),
              parsePercent(filteredRow.q2),
              parsePercent(filteredRow.q3),
              parsePercent(filteredRow.maxv)
            ],
            lq_time: filteredRow.lq_time,
            serial: filteredRow.serial,
            uuid: filteredRow.uuid,
            branch_no: filteredRow.branch_no,
            map: filteredRow.map
          });
        });
        tmpSeries.push({
          name: String(branchNo),
          type: 'boxplot',
          data: datas
        });
      });
    }

    setSeries(tmpSeries);
  }, [props.source, props.xlist]);

  useEffect(() => {
    if (props.searchType === SearchType.WORK) {
      //検査ごと
      setExportFields({
        検査日時: t('検査日時'),
        シリアルナンバー: t('シリアルナンバー'),
        検査部位: t('検査部位'),
        検査追番号: t('検査追番号'),
        ＯＫ率: t('ＯＫ率(%)')
      });
    } else {
      setExportFields({
        検査日時: t('検査日時'),
        検査部位: t('検査部位'),
        検査追番号: t('検査追番号'),
        最大: t('最大'),
        Q3: t('Q3'),
        中央: t('中央'),
        Q1: t('Q1'),
        最小: t('最小')
      });
    }
  }, [props.searchType]);

  useEffect(() => {
    if (series.length === 0) return;

    if (props.searchType === SearchType.WORK) {
      //検査ごと
      const _exportData = series[0].data.map(rdata => {
        return {
          検査日時: fmtYYYYMMDDHHmmss(rdata.lq_time),
          シリアルナンバー: rdata.serial || rdata.uuid,
          検査部位: rdata.map,
          検査追番号: rdata.branch_no,
          ＯＫ率: rdata.value
        };
      });
      setExportData(_exportData);
    } else {
      const _exportData: any = [];

      series.forEach(s => {
        s.data.forEach(rdata => {
          if (isEmpty(rdata)) {
            return;
          }
          _exportData.push({
            検査日時: rdata.lq_time,
            検査部位: rdata.map,
            検査追番号: rdata.branch_no,
            最大: rdata.value[4],
            Q3: rdata.value[3],
            中央: rdata.value[2],
            Q1: rdata.value[1],
            最小: rdata.value[0]
          });
        });
      });
      setExportData(_exportData);
    }
  }, [xAxisData, series, props.searchType]);

  return (
    <LFCBaseCharts
      option={{
        title: {
          text: props.title
        },
        grid: {
          top: 60,
          bottom: 60,
          right: 5,
          left: 60
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          formatter: `${t('検査追番号')}{name}`
        },
        tooltip: {
          trigger: 'item',
          formatter: function (params: any) {
            if (params.seriesType === 'effectScatter') {
              return `${params.name}<br>
                  <br>
                  <span style="font-weight:bold">${t('検査日時')}</span>: ${dayjs(
                    params.data.lq_time
                  ).format('YYYY-MM-DD HH:mm:ss')}<br/>
                  <span style="font-weight:bold">${t('シリアルナンバー')}</span>: ${
                    params.data.serial || params.data.uuid
                  }<br/>
                  <span style="font-weight:bold">${t('検査追番号')}</span>: ${
                    params.data.branch_no
                  }<br/>
                  <span style="font-weight:bold">${t('ＯＫ率(%)')}</span>: ${fmtPercent(
                    params.value
                  )}`;
            } else {
              // [min,  Q1,  median (or Q2),  Q3,  max]
              return `${params.name}<br/>
                  <span style="font-weight:bold">${t('検査追番号')}</span>: ${
                    params.seriesName
                  }<br/>
                  <br/>
                  <span style="font-weight:bold">${t('最大')}</span>: ${fmtPercent(
                    params.value[5]
                  )}<br/>
                  <span style="font-weight:bold">Q3</span>: ${fmtPercent(params.value[4])}<br/>
                  <span style="font-weight:bold">${t('中央')} (or Q2)</span>: ${fmtPercent(
                    params.value[3]
                  )}<br/>
                  <span style="font-weight:bold">Q1</span>: ${fmtPercent(params.value[2])}<br/>
                  <span style="font-weight:bold">${t('最小')}</span>: ${fmtPercent(
                    params.value[1]
                  )}`;
            }
          }
        },
        dataZoom: [
          {
            type: 'inside'
          }
        ],

        xAxis: {
          type: 'category',
          name: '',
          data: xAxisData,
          axisLabel: {
            rotate: '20',
            fontSize: 10
          }
        },
        yAxis: {
          type: 'value',
          name: '(%)',
          min: 0,
          max: 100
        },
        series: series
      }}
      exportData={exportData}
      exportFields={exportFields}
      exportFilename={props.exportFilename}
      height={props.height ? props.height : '100px'}
      onEvents={{
        click: (params: any) => {
          if (props.onClick != null) {
            props.onClick(params);
          }
        }
      }}
      zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
      zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
      zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
      zoomStatus={props.zoomChartId === props.zoomedChartId}
    />
  );
};
